import { Button } from "../../../../components/ui/button";
import { DialogContent, DialogHeader } from "../../../../components/ui/dialog";
import { useNavigate, useParams } from "react-router-dom";

import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormMessage,
} from "../../../../components/ui/form";
import { Textarea } from "../../../../components/ui/textarea";
import StarIcon from "../../../../assets/svgs/common/star.svg";
import { useToast } from "../../../../hooks/use-toast";
import { handleHttpError } from "../../../../utils/utils";
import { usePayoutRejectAdminHttp } from "../../../../hooks/http/admin/payouts/payout-reject.admin.http";
import usePayoutsStore from "../../../../store/payouts.store";
import Spinner from "../../../../components/spinner.components";

const FormSchema = z.object({
	rejection_reason: z.string().min(5, "Please provide a reason for rejection"),
});

export const RejectPayoutModalPartial: React.FunctionComponent = () => {
	const { payoutId } = useParams();
	const { execute: rejectPayoutHttp, fetching } = usePayoutRejectAdminHttp();
	const navigate = useNavigate();
	const { removeById } = usePayoutsStore();
	const { toast } = useToast();

	const form = useForm<z.infer<typeof FormSchema>>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			rejection_reason: "",
		},
	});

	function rejectUser(id: string) {
		removeById(id);
		navigate("/admin/payouts");
	}

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		if (!payoutId) return;
		const response = await rejectPayoutHttp({
			body: { message: data.rejection_reason },
			pathParams: { id: payoutId },
		});
		if (!response.success) {
			return handleHttpError(response, toast);
		}

		rejectUser(payoutId);
	}

	return (
		<DialogContent className="flex flex-col items-center gap-5 border-border w-full text-center text-primary">
			<DialogHeader className="w-full">
				<div className="flex flex-col">
					<p className="flex gap-1 font-semibold text-28 leading-8">
						Reason for rejecting payout request <StarIcon />
					</p>
					<span className="text-secondary">
						Give a brief for the rejection of the payout request
					</span>
				</div>
			</DialogHeader>
			<div className="border-b border-border w-full" />
			<div className="flex items-center gap-4 w-full">
				<Form {...form}>
					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="relative flex flex-col space-y-8 w-full"
					>
						<FormField
							control={form.control}
							name="rejection_reason"
							render={({ field }) => (
								<FormItem className="text-start">
									<FormControl>
										<Textarea
											className="px-6 pt-5 border border-border rounded-xl h-48 font-semibold placeholder:text-secondary resize-none"
											placeholder="describe the reason for the payout rejection here..."
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<Button
							type="submit"
							disabled={fetching}
							className="bg-rejectbuttonbg border border-rejectbuttonborder w-24 h-10 font-semibold text-rejectbuttontext tracking-wider self-end"
						>
							{fetching ? <Spinner width={15} /> : "Reject"}
						</Button>
					</form>
				</Form>
			</div>
		</DialogContent>
	);
};